import { useEffect, useRef, useState } from 'react';
import { setStateData } from '../helpers/state';
import { usePDFStore } from '../../store/pdf/store';
import { uploadLogo, deleteLogo } from '../../service/branding';
import {
    getProcessedImageForSave,
    resizeDataURLImageAsync
} from '../helpers/branding';
import { logoConfig, brandingTerms as bt } from '../../constants/branding';
import { showToast, toastTypes } from '../helpers/toast';
import { toastMessages } from '../../constants';

export const useBranding = () => {
    const uploaderRef = useRef();
    const [state, setState] = useState({
        shouldShowLogoUploader: false,
        shouldShowRemoveLogoModal: false,
        shouldShowUpdateLogoModal: false,
        isLogoLoading: false,
        isUploaderLoading: false,
        isDeletingLogo: false,
        isUpdatingLogo: false,
        toastProps: undefined,
        pendingUploadFile: null,
    });

    const { fetchLogo, setLogoSrc, logoSrc } = usePDFStore((state) => ({
        fetchLogo: state.fetchLogo,
        setLogoSrc: state.setLogoSrc,
        logoSrc: state.logoSrc
    }));

    const setShouldShowLogoUploader = (value) =>
        setStateData('shouldShowLogoUploader', value, setState);
    const setShouldShowRemoveLogoModal = (value) =>
        setStateData('shouldShowRemoveLogoModal', value, setState);
    const setShouldShowUpdateLogoModal = (value) =>
        setStateData('shouldShowUpdateLogoModal', value, setState);
    const setIsLogoLoading = (value) =>
        setStateData('isLogoLoading', value, setState);
    const setIsUploaderLoading = (value) =>
        setStateData('isUploaderLoading', value, setState);
    const setIsDeletingLogo = (value) =>
        setStateData('isDeletingLogo', value, setState);
    const setIsUpdatingLogo = (value) =>
        setStateData('isUpdatingLogo', value, setState);
    const setPendingUploadFile = (value) =>
        setStateData('pendingUploadFile', value, setState);

    const setters = {
        setShouldShowLogoUploader,
        setShouldShowRemoveLogoModal,
        setShouldShowUpdateLogoModal,
        setIsLogoLoading,
        setIsUploaderLoading,
        setIsDeletingLogo,
        setIsUpdatingLogo,
        setPendingUploadFile,
    };

    useEffect(() => {
        if (state.shouldShowLogoUploader) return;
        const loadLogo = async () => {
            setIsLogoLoading(true);
            await fetchLogo();
            setIsLogoLoading(false);
        };
        loadLogo();
    }, [state.shouldShowLogoUploader]);

    const handleOnLogoUpload = async (files) => {
        if (files.length !== 1) return;

        if (logoSrc) {
            setPendingUploadFile(files[0]);
            setShouldShowUpdateLogoModal(true);
            return;
        }

        await uploadLogoProcess(files[0]);
    };

    const uploadLogoProcess = async (uppyFile) => {
        try {
            setIsUploaderLoading(true);
            setIsUpdatingLogo(true);
            const base64ResizedUppyFile = await getProcessedImageForSave({
                uppyFile,
                targetWidth: logoConfig.MAX_WIDTH,
                targetHeight: logoConfig.MAX_HEIGHT
            });
            const base64ResizedUppyString = base64ResizedUppyFile.split(',')[1];
            const response = await uploadLogo(base64ResizedUppyString);
            if (!response?.url) {
                setShouldShowUpdateLogoModal(false);
                throw new Error(response.error || `HTTP Error: ${response}`);
            }
            const processImage = async () => {
                try {
                    const resizedDataURL = await resizeDataURLImageAsync(base64ResizedUppyFile);
                    return resizedDataURL;
                } catch (err) {
                    console.error('Resize failed:', err);
                }
            };

            const imageUrlForBrandingPage = await processImage();
            setLogoSrc(imageUrlForBrandingPage);
            setShouldShowLogoUploader(false);
            setShouldShowUpdateLogoModal(false);
            showToast(toastTypes.success, toastMessages.success.brandingLogo, setState);
        } catch (error) {
            console.error(bt.errorMessages.logoUploadError, error);
            showToast(toastTypes.error, toastMessages.error.brandingLogo, setState);
        } finally {
            setIsUploaderLoading(false);
            setIsUpdatingLogo(false);
        }
    };

    const handleConfirmUpdateLogo = async () => {
        if (state.pendingUploadFile) {
            await uploadLogoProcess(state.pendingUploadFile);
            setPendingUploadFile(null);
        }
    };

    const handleRemoveLogo = async () => {
        try {
            setIsDeletingLogo(true);
            const response = await deleteLogo();
            setShouldShowRemoveLogoModal(false);
            if (response?.success !== 'true') throw new Error(response.error || `HTTP Error: ${response}`);
            setLogoSrc(undefined);
            showToast(toastTypes.success, toastMessages.success.brandingLogoDelete, setState);
        } catch (error) {
            console.error(bt.errorMessages.logoDeleteError, error);
            showToast(toastTypes.error, toastMessages.error.brandingLogoDelete, setState);
        } finally {
            setIsDeletingLogo(false);
        }
    };

    return {
        uploaderRef,
        state,
        setters,
        handleOnLogoUpload,
        handleRemoveLogo,
        handleConfirmUpdateLogo,
        logoSrc,
    };
};
